import { useEffect, useId, useState } from 'react'
import { createPortal } from 'react-dom'
import type { BuildProgressSnapshot, WatchBuildResult } from '../api/client'
import {
  isCompleteCompatibleBuild,
  type ImageLookupState,
  type StudioState,
} from '../studioState'
import { AssembledPreview } from './AssembledPreview'
import { BuildProgressMini } from './BuildProgressMini'
import { PreviewGenerating } from './PreviewGenerating'

type WatchStageProps = {
  buildResult: WatchBuildResult | null
  isSending: boolean
  loadingLabel?: string
  buildProgress?: BuildProgressSnapshot | null
  assembledImageUrl: string | null
  isGeneratingImage: boolean
  imageError: string | null
  onGenerateImage: () => void
  studioState: StudioState
  imageLookupState: ImageLookupState
}

export function WatchStage({
  buildResult,
  isSending,
  loadingLabel,
  buildProgress,
  assembledImageUrl,
  isGeneratingImage,
  imageError,
  onGenerateImage,
  studioState,
  imageLookupState,
}: WatchStageProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const lightboxTitleId = useId()
  const showProgress = isSending && (buildProgress || loadingLabel)
  const isImageComplete = buildResult ? isCompleteCompatibleBuild(buildResult) : false
  const partImages = buildResult
    ? Object.entries(buildResult.build.parts)
        .filter(([, part]) => Boolean(part.image_url))
        .slice(0, 4)
    : []

  useEffect(() => {
    if (!assembledImageUrl) setLightboxOpen(false)
  }, [assembledImageUrl])

  useEffect(() => {
    if (!lightboxOpen) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setLightboxOpen(false)
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [lightboxOpen])

  const focusPartInLoupe = (partType: string) => {
    const target = document.getElementById(`loupe-part-${partType}`)
    if (!target) return
    target.scrollIntoView({ behavior: 'smooth', block: 'center' })
    target.classList.remove('loupe-row--flash')
    // Restart highlight animation if the same tile is tapped again.
    void target.offsetWidth
    target.classList.add('loupe-row--flash')
    window.setTimeout(() => target.classList.remove('loupe-row--flash'), 1300)
    const expandBtn = target.querySelector<HTMLButtonElement>(
      '.loupe-row-expand[aria-expanded="false"]',
    )
    expandBtn?.click()
  }

  const stageTitle = buildResult
    ? studioState.phase === 'needs_adjustment'
      ? 'Review and refine'
      : assembledImageUrl
        ? 'Studio preview'
        : 'Selected parts'
    : isSending
      ? 'Building your watch…'
      : 'Awaiting your brief'

  return (
    <section className="watch-stage" aria-labelledby="watch-stage-heading">
      <header className="watch-stage-header">
        <h2 id="watch-stage-heading" className="stage-title">
          Your Watch
        </h2>
        <p className="stage-subtitle">{stageTitle}</p>
      </header>

      <div className="watch-stage-body">
        {showProgress ? (
          <div className="build-live-status" role="status" aria-live="polite">
            {buildProgress ? (
              <BuildProgressMini progress={buildProgress} fallbackLabel={loadingLabel} variant="stage" />
            ) : (
              <div className="stage-progress-prelude">
                <span className="build-dot-pulse" />
                <span className="build-dot-pulse" />
                <span className="build-dot-pulse" />
                <div>
                  <p className="stage-progress-label">{studioState.label}</p>
                  <p className="stage-progress-detail">{studioState.detail}</p>
                </div>
              </div>
            )}
          </div>
        ) : null}

        <div className="watch-stage-frame" aria-live="polite">
          {isGeneratingImage ? (
            <PreviewGenerating
              isGenerating={isGeneratingImage}
              isComplete={Boolean(assembledImageUrl) && !isGeneratingImage}
            />
          ) : assembledImageUrl ? (
            <figure className="watch-stage-image">
              <button
                type="button"
                className="watch-stage-image-button"
                onClick={() => setLightboxOpen(true)}
                aria-haspopup="dialog"
                aria-label="Open full-size watch preview"
              >
                <img
                  src={assembledImageUrl}
                  alt="Exact assembled preview of your selected watch parts"
                />
              </button>
              <figcaption className="cached-preview-label">Tap to enlarge</figcaption>
            </figure>
          ) : partImages.length > 0 ? (
            <div className="watch-parts-overview">
              <div className="watch-parts-collage" aria-label="Selected part images">
                {partImages.map(([partType, part]) => (
                  <button
                    key={`${partType}-${part.id}`}
                    type="button"
                    className="watch-part-tile"
                    onClick={() => focusPartInLoupe(partType)}
                    aria-label={`View ${partType}: ${part.name}`}
                  >
                    <img src={part.image_url ?? ''} alt="" />
                    <span className="watch-part-tile-caption">{partType}</span>
                  </button>
                ))}
              </div>
              <p className="watch-stage-hint">
                {isImageComplete
                  ? 'Tap a part to open its details below.'
                  : 'Tap a part to review it below, then refine any unresolved slots.'}
              </p>
            </div>
          ) : (
            <div className="watch-stage-empty" aria-hidden={isSending ? undefined : 'true'}>
              <svg className="watch-stage-icon" viewBox="0 0 64 64" fill="none">
                <circle cx="32" cy="32" r="20" stroke="currentColor" strokeWidth="1.25" />
                <circle cx="32" cy="32" r="14" stroke="currentColor" strokeWidth="0.6" opacity="0.5" />
                <line x1="32" y1="32" x2="32" y2="20" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
                <line x1="32" y1="32" x2="40" y2="32" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
                <rect x="30" y="10" width="4" height="4" fill="currentColor" opacity="0.6" />
                <rect x="30" y="50" width="4" height="4" fill="currentColor" opacity="0.6" />
                <rect x="49" y="30" width="4" height="4" fill="currentColor" opacity="0.6" />
              </svg>
              <p className="watch-stage-hint">
                {buildResult
                  ? 'No compatible parts were selected yet. Refine your request below.'
                  : 'Describe your watch. Horatix will assemble it here.'}
              </p>
              <span className="watch-stage-coord">Horatix Studio</span>
            </div>
          )}

        </div>

        {buildResult ? (
          <>
            {isImageComplete ? (
              <div className={`cache-lookup-state cache-lookup-state--${imageLookupState}`}>
                {imageLookupState === 'looking'
                  ? 'Checking for an exact saved preview…'
                  : imageLookupState === 'found'
                    ? 'Exact saved preview found.'
                    : imageLookupState === 'missing'
                      ? 'No saved preview yet. Your part summary is ready now.'
                      : imageLookupState === 'unavailable'
                        ? 'Saved preview lookup is unavailable. Your parts are unaffected.'
                        : 'Preparing preview lookup…'}
              </div>
            ) : null}
            <div className="watch-stage-actions">
                <AssembledPreview
                    buildResult={buildResult}
                    isSending={isSending}
                    isGenerating={isGeneratingImage}
                    imageUrl={assembledImageUrl}
                    error={imageError}
                    onGenerate={onGenerateImage}
                />
              </div>
          </>
        ) : null}
      </div>

      {lightboxOpen && assembledImageUrl
        ? createPortal(
            <div
              className="watch-image-lightbox"
              role="dialog"
              aria-modal="true"
              aria-labelledby={lightboxTitleId}
              onClick={() => setLightboxOpen(false)}
            >
              <div className="watch-image-lightbox-toolbar">
                <p id={lightboxTitleId} className="watch-image-lightbox-title">
                  Exact build preview
                </p>
                <button
                  type="button"
                  className="watch-image-lightbox-close"
                  onClick={() => setLightboxOpen(false)}
                  aria-label="Close preview"
                >
                  Close
                </button>
              </div>
              <div
                className="watch-image-lightbox-frame"
                onClick={(event) => event.stopPropagation()}
              >
                <img
                  src={assembledImageUrl}
                  alt="Exact assembled preview of your selected watch parts"
                />
              </div>
              <p className="watch-image-lightbox-hint">Pinch to zoom · Tap outside to close</p>
            </div>,
            document.body,
          )
        : null}
    </section>
  )
}
